import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { formatMoney } from "@/lib/pricing";
import JeaToggle from "@/components/jea/JeaToggle";
import { BUNDLES, detectBundleFromSlugs } from "@/lib/bundles";
import { cn } from "@/lib/utils";

const BrokerRow = ({ broker, enabled, onToggle, accountType }) => (
  <div
    className="flex items-center gap-4 bg-bg-primary border border-border-default rounded-[8px] p-4"
    data-testid={`broker-row-${broker.slug}`}
  >
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-white font-semibold text-[14px]">{broker.name}</div>
        <span className="text-[11px] px-2 py-0.5 rounded bg-bg-tertiary text-text-secondary">
          {broker.category}
        </span>
      </div>
      <div className="text-text-secondary text-[13px] mt-1 leading-5">{broker.description}</div>
    </div>
    <div className="shrink-0 flex items-center gap-2">
      {accountType === "pro" && (
        <span
          className="text-[10px] px-2 py-0.5 rounded bg-accent-pink/15 text-accent-pink font-bold tracking-wider border border-accent-pink/40"
          data-testid={`broker-pro-badge-${broker.slug}`}
        >
          PRO RATE
        </span>
      )}
      <div
        className={cn(
          "jea-mono font-bold text-[14px]",
          accountType === "pro" ? "text-accent-pink" : "text-white"
        )}
        data-testid={`broker-price-${broker.slug}`}
      >
        {formatMoney(broker.price_cents)}
      </div>
    </div>
    <JeaToggle
      checked={enabled}
      onChange={(v) => onToggle(broker.id, v)}
      data-testid={`broker-toggle-${broker.slug}`}
    />
  </div>
);

const BundleChip = ({ bundle, active, onClick, testid }) => {
  const Icon = bundle.Icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={cn(
        "flex items-start gap-3 p-4 rounded-[10px] border transition-all text-left",
        active
          ? "border-accent-pink bg-accent-pink/10 shadow-pink-glow"
          : "border-border-default bg-bg-primary hover:border-accent-pink/50"
      )}
    >
      <div className={cn(
        "w-10 h-10 rounded-full flex items-center justify-center shrink-0",
        active ? "bg-accent-pink text-bg-primary" : "bg-bg-tertiary text-accent-pink"
      )}>
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className={cn("font-bold text-[14px]", active ? "text-accent-pink" : "text-white")}>
            {bundle.label}
          </div>
          {active && <Check size={14} className="text-accent-pink" />}
        </div>
        <div className="text-text-secondary text-[12px] mt-0.5">{bundle.tagline}</div>
        <div className="text-text-muted text-[11px] mt-1">{bundle.slugs.length} brokers</div>
      </div>
    </button>
  );
};

const BrokerStep = ({ data, setSection, accountType }) => {
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const prefs = data.brokers || {};

  useEffect(() => {
    const boot = async () => {
      try {
        const { data: res } = await apiClient.get("/brokers");
        setBrokers(res.brokers || []);
        if (Object.keys(prefs).length === 0) {
          const initial = {};
          (res.brokers || []).forEach((b) => (initial[b.id] = true));
          setSection("brokers", initial);
        }
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (id, v) => setSection("brokers", { ...prefs, [id]: v });

  // Determine which bundle (if any) matches the current selection
  const activeBundle = useMemo(() => {
    if (brokers.length === 0) return null;
    const enabledSlugs = brokers
      .filter((b) => prefs[b.id] !== false)
      .map((b) => b.slug);
    return detectBundleFromSlugs(enabledSlugs);
  }, [brokers, prefs]);

  const applyBundle = (bundleKey) => {
    const bundle = BUNDLES.find((b) => b.key === bundleKey);
    if (!bundle) return;
    const next = {};
    brokers.forEach((b) => {
      next[b.id] = bundle.slugs.includes(b.slug);
    });
    setSection("brokers", next);
  };

  return (
    <div className="space-y-6" data-testid="broker-step">
      <div className="space-y-2">
        <h2 className="text-white font-bold text-[22px]">Set Your Default Brokers</h2>
        <p className="text-text-secondary text-[15px]">
          Choose which data brokers to enable by default. You can always change these per query.
        </p>
      </div>

      {!loading && (
        <div data-testid="broker-bundle-chips">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {BUNDLES.map((b) => (
              <BundleChip
                key={b.key}
                bundle={b}
                active={activeBundle === b.key}
                onClick={() => applyBundle(b.key)}
                testid={`bundle-chip-${b.key}`}
              />
            ))}
          </div>
          <p className="text-text-secondary text-[13px] mt-3">
            Or select individual brokers below. You can always change these on a per-query basis.
          </p>
        </div>
      )}

      {loading ? (
        <div className="h-40 flex items-center justify-center text-text-secondary text-[13px]">
          Loading brokers…
        </div>
      ) : (
        <div className="space-y-2" data-testid="broker-list">
          {brokers.map((b) => (
            <BrokerRow
              key={b.id}
              broker={b}
              enabled={prefs[b.id] !== false}
              onToggle={toggle}
              accountType={accountType}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BrokerStep;
